import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { RefreshCw, Star, AlertTriangle, Mail } from 'lucide-react';

export default function DiagnosticPanel({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().get('/config/notification-diagnostic');
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo cargar el diagnóstico');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground" data-testid="diagnostic-loading">Cargando diagnóstico…</div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="diagnostic-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Quién recibe <b>hoy</b> cada notificación (con la configuración actual). Los <Star className="inline w-3 h-3 text-emerald-500" /> son <b>@cmidiomas.com</b>.
        </p>
        <Button variant="ghost" size="sm" onClick={load} data-testid="diagnostic-reload">
          <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
        </Button>
      </div>

      {data.users_without_email?.length > 0 && (
        <Card className="border-amber-400/60 bg-amber-50/60 dark:bg-amber-950/20" data-testid="diagnostic-no-email">
          <CardContent className="pt-4">
            <p className="text-sm font-medium flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4" /> Usuarios activos SIN correo (nunca reciben nada)
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {data.users_without_email.map((u) => (
                <Badge key={u.id} variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-400">
                  {u.name} · {u.role}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {data.types.map((t) => (
          <Card key={t.key} data-testid={`diagnostic-row-${t.key}`}>
            <CardHeader className="py-3 cursor-pointer" onClick={() => setExpanded((s) => ({ ...s, [t.key]: !s[t.key] }))}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" /> {t.label}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {t.count === 0 ? (
                    <Badge variant="destructive" data-testid={`diagnostic-count-${t.key}`}>0 destinatarios ⚠️</Badge>
                  ) : (
                    <Badge variant="secondary" data-testid={`diagnostic-count-${t.key}`}>{t.count} destinatario(s)</Badge>
                  )}
                  <Badge variant="outline" className="text-emerald-600 border-emerald-500">
                    <Star className="w-3 h-3 mr-1" />{t.cm_count} cm
                  </Badge>
                </div>
              </div>
            </CardHeader>
            {expanded[t.key] && (
              <CardContent className="pt-0 pb-3">
                {t.count === 0 ? (
                  <p className="text-xs text-red-500">Nadie recibirá esta notificación.</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {t.recipients.map((r) => (
                      <Badge key={r.email} variant={r.is_cm ? 'default' : 'outline'} className="flex items-center gap-1">
                        {r.is_cm && <Star className="w-3 h-3" />}{r.email}
                        {r.role === 'externo' && <span className="opacity-70">(ext)</span>}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
